import { SearchSuggestion } from '@src/types/Filters';
import { Suggestions } from '../Suggestions';
import { ProductItem } from '../FilterItem/ProductItem';
import { useMediaFindFacet } from '@src/queries/media/useQueryMediaFind';
import { useQueryProducts } from '@src/queries/products/useQueryProducts';
import { useAtom, useAtomValue } from 'jotai';
import {
  FacetState,
  filterProductExactInputState,
  filterProductSimilarInputState,
} from '@src/pages/Discover/components/Atoms/Atoms';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import { onFilterChange } from '@src/fragments/Filters/utils/FilterStateUtility';
import { ArrayHelper } from '@src/services/helpers/array';
import { LightSearchBar } from '@src/components/LightSearchBar';
import { Row } from '@src/components/Utils';
import { useDebounce } from 'use-debounce';
import {
  productExactExcludeState,
  productSimilarExcludeState,
} from '@src/pages/Discover/components/Selectors/Selectors';
import { ProductView } from 'common';
import { useFiltersParams } from '@src/hooks/useFiltersParams';

const MAX_PRODUCT_SEARCH_LENGTH = 128;

export function addProductNameToSuggestions(suggestions: SearchSuggestion[], resFilterProduct: ProductView[] = []): SearchSuggestion[] {
  const products = ArrayHelper.mapToObject(resFilterProduct, (p) => p.id);
  return suggestions.map((suggestion) => {
    const rawKey = XclusionUtility.getRawKey(suggestion.key);
    const product = products[rawKey];
    return {
      ...suggestion,
      display: `${rawKey} ${product?.title || ''}`,
    };
  });
}

export function ProductsSuggestion({
  productType,
  inputTitle,
}: {
  productType: 'productExact' | 'productSimilar';
  inputTitle: string;
}) {
  const { filters, setFilters } = useFiltersParams();
  const facetState = useAtomValue(FacetState);

  const matchesSearch = (display: string, filterInput: string): boolean =>
    filterInput === '' || display.toLowerCase().includes(filterInput.toLowerCase());

  const [filterInput, setFilterInput] = useAtom(
    productType === 'productExact' ? filterProductExactInputState : filterProductSimilarInputState,
  );
  const filterExclude = useAtomValue(productType === 'productExact' ? productExactExcludeState : productSimilarExcludeState);
  const normalizedFilterInput = filterInput.trim();
  const [debouncedFilterInput] = useDebounce(normalizedFilterInput, facetState.typingDebounce);

  // Products
  const facetType = productType === 'productExact' ? 'productIdsExact' : 'productIdsSimilar';
  const { data: resFilterProductIds, isFetching: isFetchingFilterProduct } = useMediaFindFacet(
    facetState.mediaConstraints,
    debouncedFilterInput,
    facetType,
    0,
    facetState.enableFacet
  );

  const productIds = resFilterProductIds?.pages[0].facets?.[facetType] ?? [];
  const { data: resFilterProduct, isFetching: isFetchingAllProducts } = useQueryProducts(productIds, {
    enabled: productIds.length > 0,
  });

  const productIdsValues = addProductNameToSuggestions(
    productIds.map((p) => ({ key: p, display: p })),
    resFilterProduct
  ).filter((s) => matchesSearch(s.display, normalizedFilterInput));

  const selectedProductIdsValues = addProductNameToSuggestions(
    filters[productType].map((p) => ({ key: p, display: XclusionUtility.getRawKey(p) })),
    resFilterProduct
  );

  const isLoading = debouncedFilterInput !== normalizedFilterInput
    || isFetchingFilterProduct
    || isFetchingAllProducts
    || (!facetState.enableFacet && normalizedFilterInput.length > 0);

  return (
    <>
      <Row>
        <LightSearchBar
          onText={setFilterInput}
          value={filterInput}
          isLoading={isLoading}
          inputMaxLength={MAX_PRODUCT_SEARCH_LENGTH}
          title={inputTitle}
        />
      </Row>
      <Suggestions
        onChange={onFilterChange(setFilters, productType)}
        selectedValues={renderValues(selectedProductIdsValues)}
        values={renderValues(productIdsValues)}
        exclude={filterExclude}
      />
    </>
  );
}

function renderValues(suggestions: SearchSuggestion[]) {
  return suggestions.map((suggestion) => ({
    key: suggestion.key,
    children: <ProductItem suggestion={suggestion} />,
  }));
}
